import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { Search, MapPin, Award, Loader2, Ticket, User, Calendar, Clock } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';

const SESSION_OPTIONS = [
  { value: '', label: '— Selecionar —' },
  { value: 'fisioterapia', label: 'Fisioterapia' },
  { value: 'nutricao', label: 'Nutrição' },
  { value: 'personal', label: 'Personal Trainer' },
  { value: 'outro', label: 'Outro' },
];

const ProfessionalNetworkPage = () => {
  const location = useLocation();
  const [pros, setPros] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ category: '', city: '', specialty: '', q: '' });
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [confirmPro, setConfirmPro] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sessionForm, setSessionForm] = useState({ session_type: '', preferred_date: '', preferred_time: '', notes: '' });

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

  // Pre-fill session_type from URL (?session=fisioterapia|nutricao|personal)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('session');
    if (s && ['fisioterapia', 'nutricao', 'personal'].includes(s)) {
      setSessionForm((f) => ({ ...f, session_type: s }));
    }
  }, [location.search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      const [proRes, balRes] = await Promise.all([
        axios.get(`${API}/professionals?${params}`, authHeader()),
        axios.get(`${API}/vitalpass/balance`, authHeader()),
      ]);
      setPros(proRes.data.professionals || []);
      setCategories(proRes.data.categories || []);
      setBalance(balRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar profissionais');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleRequest = async () => {
    if (!confirmPro) return;
    if ((balance?.balance ?? 0) <= 0) {
      toast.error('Você não possui VITALPASS disponível');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/vitalpass/request-professional`, {
        professional_id: confirmPro.professional_id,
        session_type: sessionForm.session_type || null,
        preferred_date: sessionForm.preferred_date || null,
        preferred_time: sessionForm.preferred_time || null,
        notes: sessionForm.notes || '',
      }, authHeader());
      toast.success(sessionForm.preferred_date ? 'Sessão agendada!' : 'Solicitação registrada!');
      setBalance((b) => ({ ...b, balance: data.new_balance }));
      setConfirmPro(null);
      setSessionForm({ session_type: '', preferred_date: '', preferred_time: '', notes: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao solicitar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4" data-testid="professional-network-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Profissionais Parceiros
        </h2>
        {balance && balance.has_vitalpass && (
          <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl px-4 py-2 flex items-center gap-2 shadow" data-testid="vitalpass-badge">
            <Ticket className="w-5 h-5" />
            <div>
              <p className="text-[10px] uppercase font-semibold tracking-wide leading-none">VITALPASS</p>
              <p className="text-lg font-black leading-none">{balance.balance}/{balance.total}</p>
            </div>
          </div>
        )}
      </div>

      {!balance?.has_vitalpass && (
        <Card className="p-4 bg-amber-50 border-amber-200 mb-4 text-sm">
          Seu plano atual não inclui VITALPASS. Faça upgrade para um plano <strong>Vital Personal</strong> para acessar a rede de profissionais.
        </Card>
      )}

      {/* Filters */}
      <Card className="p-3 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="h-10 rounded-md border border-input px-3 text-sm"
            data-testid="profnet-filter-category"
          >
            <option value="">Todas categorias</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <Input
            placeholder="Especialidade..."
            value={filters.specialty}
            onChange={(e) => setFilters({ ...filters, specialty: e.target.value })}
            data-testid="profnet-filter-specialty"
          />
          <Input
            placeholder="Cidade..."
            value={filters.city}
            onChange={(e) => setFilters({ ...filters, city: e.target.value })}
            data-testid="profnet-filter-city"
          />
          <div className="relative">
            <Search className="w-4 h-4 absolute top-3 left-3 text-slate-400" />
            <Input
              placeholder="Nome ou registro..."
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="pl-10"
              data-testid="profnet-filter-q"
            />
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>
      ) : pros.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="profnet-empty">
          Nenhum profissional encontrado com esses filtros.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {pros.map((p) => (
            <Card key={p.professional_id} className="p-4 hover:shadow-md transition-shadow" data-testid={`pro-card-${p.professional_id}`}>
              <div className="flex items-start gap-3 mb-2">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center overflow-hidden">
                  {p.photo_base64 ? (
                    <img src={p.photo_base64} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-teal-700" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900">{p.name}</h3>
                  <p className="text-xs text-slate-500">{p.formation}</p>
                </div>
              </div>
              <p className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1"><Award className="w-3 h-3" /> Registro: {p.registration_number}</p>
              <div className="flex flex-wrap gap-1 my-2">
                {(p.categories || []).map((c) => <span key={c} className="bg-teal-100 text-teal-800 text-[10px] font-semibold px-2 py-0.5 rounded-full">{c}</span>)}
              </div>
              {(p.specialties || []).length > 0 && (
                <p className="text-xs text-slate-600 mb-2 line-clamp-2">
                  <strong>Especialidades:</strong> {p.specialties.join(', ')}
                </p>
              )}
              {(p.locations || []).length > 0 && (
                <div className="text-[10px] text-slate-500 space-y-0.5">
                  {p.locations.slice(0, 2).map((l, idx) => (
                    <p key={idx} className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span>
                        {l.place_name && <strong className="text-slate-700">{l.place_name} • </strong>}
                        {l.city}/{l.state}
                      </span>
                    </p>
                  ))}
                  {p.locations.length > 2 && <p className="text-slate-400">+{p.locations.length - 2} local(is)</p>}
                </div>
              )}

              {/* VITALPASS Offers */}
              {(p.vitalpass_offers || []).length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100" data-testid={`offers-${p.professional_id}`}>
                  <p className="text-[9px] uppercase tracking-wide font-bold text-teal-700 mb-1 flex items-center gap-1">
                    <Ticket className="w-2.5 h-2.5" /> Ofertas VITALPASS
                  </p>
                  <div className="space-y-1">
                    {p.vitalpass_offers.slice(0, 3).map((o) => (
                      <div key={o.id} className="flex items-start gap-1.5 text-[10px]">
                        <span className="flex-shrink-0 bg-teal-600 text-white font-black rounded px-1.5 py-0.5 min-w-[24px] text-center">
                          {o.tokens}×
                        </span>
                        <div className="flex-1 leading-tight">
                          <p className="text-slate-800 font-semibold">{o.title}</p>
                          {o.description && <p className="text-slate-500 line-clamp-1">{o.description}</p>}
                        </div>
                      </div>
                    ))}
                    {p.vitalpass_offers.length > 3 && (
                      <p className="text-[9px] text-slate-400 italic pl-7">
                        +{p.vitalpass_offers.length - 3} outra(s) oferta(s)
                      </p>
                    )}
                  </div>
                </div>
              )}
              <Button
                onClick={() => setConfirmPro(p)}
                disabled={!balance?.has_vitalpass}
                className="w-full mt-3 bg-teal-600 hover:bg-teal-700 text-white text-xs"
                data-testid={`solicitar-${p.professional_id}`}
              >
                <Ticket className="w-3 h-3 mr-1" /> Solicitar Profissional
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmPro && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setConfirmPro(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="solicitar-confirm-modal">
            <div className="text-center mb-4">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <Ticket className="w-7 h-7 text-teal-700" />
              </div>
              <h3 className="text-xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {balance?.is_premium_benefits ? 'Agendar Sessão' : 'Confirmar Solicitação'}
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Utilizará <strong>1 {balance?.unit_label || 'VITALPASS'}</strong> com <strong>{confirmPro.name}</strong>
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase font-bold text-slate-600 tracking-wide">Tipo de Sessão</label>
                <select
                  value={sessionForm.session_type}
                  onChange={(e) => setSessionForm({ ...sessionForm, session_type: e.target.value })}
                  className="w-full mt-1 h-10 rounded-md border border-input px-3 text-sm"
                  data-testid="session-type-select"
                >
                  {SESSION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] uppercase font-bold text-slate-600 tracking-wide flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Data preferida
                  </label>
                  <Input
                    type="date"
                    value={sessionForm.preferred_date}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setSessionForm({ ...sessionForm, preferred_date: e.target.value })}
                    className="mt-1"
                    data-testid="preferred-date"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase font-bold text-slate-600 tracking-wide flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Horário
                  </label>
                  <Input
                    type="time"
                    value={sessionForm.preferred_time}
                    onChange={(e) => setSessionForm({ ...sessionForm, preferred_time: e.target.value })}
                    className="mt-1"
                    data-testid="preferred-time"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase font-bold text-slate-600 tracking-wide">Observação (opcional)</label>
                <textarea
                  value={sessionForm.notes}
                  onChange={(e) => setSessionForm({ ...sessionForm, notes: e.target.value })}
                  rows={2}
                  maxLength={400}
                  placeholder="Ex.: dor no joelho, prefiro manhã..."
                  className="w-full mt-1 rounded-md border border-input px-3 py-2 text-sm resize-none"
                  data-testid="request-notes"
                />
              </div>

              <p className="text-xs text-slate-500 bg-slate-50 rounded-md p-2">
                Saldo após: <strong>{Math.max(0, (balance?.balance ?? 0) - 1)}</strong> {balance?.unit_label_plural || 'tokens'}
              </p>
            </div>

            <div className="flex gap-2 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmPro(null)} data-testid="solicitar-cancel">Cancelar</Button>
              <Button onClick={handleRequest} disabled={submitting} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-testid="solicitar-confirm">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (sessionForm.preferred_date ? 'Agendar' : 'Confirmar')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalNetworkPage;
